/**
 * Phase 264d — Header "Install Out-Play" pin, with educational fallback.
 *
 * Mounted next to NotificationBell in AppShell, visible to every
 * authenticated role on every page.  Click behaviour fans out into three
 * branches, all gesture-driven:
 *
 *   1. Chromium browsers that have already fired `beforeinstallprompt`
 *      (`canInstall === true`) — call the real native `promptInstall()`.
 *   2. iOS Safari (`isIos === true`) — open the educational modal
 *      pre-scrolled to the iOS "Share → Add to Home Screen" steps.
 *   3. Anything else (browser doesn't support PWA install, or hasn't
 *      met Chrome's engagement heuristic yet) — open the same modal
 *      with a multi-browser cheat-sheet so the user always has a path
 *      forward instead of a dead-click.
 *
 * Visibility rule: the button hides ONLY when already installed or
 * running in standalone mode.  Everyone else sees the affordance —
 * matches the user's explicit "front and center for every persona" ask.
 *
 * No `animate-pulse` — pulsing every page would be visual noise
 * indistinguishable from "broken / loading" UI.
 */
import { useCallback, useState } from 'react';
import { Download, Share, Smartphone, X, Monitor, Globe, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { usePwaInstall } from '@/lib/pwaInstall';
import { useAuth } from '@/contexts/AuthContext';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

const SOURCE = 'header_pin';

export default function HeaderInstallButton() {
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const persona = personaForRole(user?.role);

  const handleClick = useCallback(async () => {
    if (busy) return;
    // Branch 1 — native prompt available, fire it inside the click frame.
    if (canInstall && !isIos) {
      setBusy(true);
      try {
        trackPwa('pwa_install_prompted', { source: SOURCE, persona });
        const accepted = await promptInstall();
        trackPwa(accepted ? 'pwa_install_accepted' : 'pwa_install_rejected', {
          source: SOURCE, persona,
        });
        if (accepted) toast.success('Out-Play is installing on this device.');
      } catch (_e) {
        trackPwa('pwa_install_rejected', { source: SOURCE, persona, reason: 'error' });
      } finally {
        setBusy(false);
      }
      return;
    }
    // Branches 2 & 3 — show the educational fallback modal. Always log
    // the prompt so PostHog records every click intent (with surface).
    trackPwa('pwa_install_prompted', {
      source: SOURCE, persona, surface: isIos ? 'ios_safari' : 'fallback_guide',
    });
    setShowGuide(true);
  }, [busy, canInstall, isIos, persona, promptInstall]);

  // Hide ONLY when the user already runs the standalone PWA. Every other
  // user — even on browsers without a native install path — gets the
  // button + the educational fallback when they click. This is the
  // "front and center for every persona" outcome the user asked for.
  if (installed || isStandalone) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={busy}
        title="Install Out-Play on this device"
        data-testid="header-install-pwa-btn"
        className="h-8 px-2.5 text-xs font-semibold gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-800/60 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
      >
        {isIos ? <Smartphone className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
        <span className="hidden sm:inline">{busy ? 'Installing…' : 'Install'}</span>
      </Button>

      <InstallGuideModal
        open={showGuide}
        onClose={() => setShowGuide(false)}
        isIos={isIos}
        persona={persona}
      />
    </>
  );
}


/**
 * Educational fallback. Browser-agnostic cheat sheet so a user who
 * clicks "Install" on a browser that hasn't fired
 * `beforeinstallprompt` yet still knows how to install via their
 * browser's URL-bar install icon, or via iOS Safari's Share sheet.
 */
function InstallGuideModal({ open, onClose, isIos, persona }) {
  const dismiss = useCallback((outcome) => {
    trackPwa('pwa_install_rejected', {
      source: SOURCE, persona, surface: 'fallback_guide', outcome,
    });
    onClose();
  }, [onClose, persona]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss('dismissed'); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="header-install-guide-modal"
      >
        {/* Hero strip — same gradient family as Phase 264c first-login modal */}
        <div className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800 px-6 pt-6 pb-6 text-white">
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '18px 18px' }} aria-hidden="true" />
          <button
            type="button"
            onClick={() => dismiss('close_btn')}
            aria-label="Close"
            data-testid="header-install-guide-close"
            className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/25 transition"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center ring-1 ring-white/25 backdrop-blur">
              <Download className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Install guide</p>
              <DialogTitle className="text-lg font-extrabold leading-tight mt-0.5">Pin Out-Play to your device</DialogTitle>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 bg-white dark:bg-slate-950">
          <DialogDescription className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Your browser hasn&apos;t shown the one-tap install prompt yet — most browsers wait until you&apos;ve interacted with the page a few times. Here&apos;s how to install Out-Play right now from any browser:
          </DialogDescription>

          {/* iOS Safari row */}
          {isIos ? (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/30 p-3.5 space-y-2" data-testid="header-install-guide-ios">
              <p className="text-xs font-bold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5" /> On iOS Safari
              </p>
              <ol className="text-[11px] leading-relaxed text-slate-700 dark:text-slate-300 list-decimal pl-4 space-y-1">
                <li>Tap the <Share className="inline h-3 w-3 align-text-bottom" /> <strong>Share</strong> icon in Safari&apos;s bottom bar.</li>
                <li>Scroll and pick <strong>Add to Home Screen</strong>.</li>
                <li>Tap <strong>Add</strong> in the top-right corner — that&apos;s it!</li>
              </ol>
            </div>
          ) : (
            <>
              {/* Desktop Chromium row */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-3.5 space-y-2" data-testid="header-install-guide-desktop">
                <p className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                  <Monitor className="h-3.5 w-3.5" /> Desktop (Chrome / Edge / Brave)
                </p>
                <ol className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 list-decimal pl-4 space-y-1">
                  <li>Look for the <strong className="font-mono text-slate-900 dark:text-slate-100">⊕</strong> install icon at the right edge of the URL bar.</li>
                  <li>Click it, then click <strong>Install</strong> in the popup.</li>
                  <li>Out-Play opens in its own window — no more browser tabs.</li>
                </ol>
              </div>

              {/* Mobile Chromium row */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-3.5 space-y-2" data-testid="header-install-guide-android">
                <p className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                  <Smartphone className="h-3.5 w-3.5" /> Mobile (Android Chrome)
                </p>
                <ol className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 list-decimal pl-4 space-y-1">
                  <li>Tap the <strong>⋮</strong> (three-dot) menu in Chrome&apos;s top-right corner.</li>
                  <li>Pick <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</li>
                  <li>Confirm — Out-Play lands on your home screen like a native app.</li>
                </ol>
              </div>
            </>
          )}

          {/* Help footer */}
          <div className="flex items-start gap-2 text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
            <HelpCircle className="h-3 w-3 mt-0.5 shrink-0" />
            <p>
              Don&apos;t see any of these options? You&apos;re likely on Firefox or a private window — both block PWA install by design. Switch to Chrome / Edge / Brave / Safari and try again, or just keep using Out-Play in the browser.
            </p>
          </div>
        </div>

        <div className="px-6 pb-5 bg-white dark:bg-slate-950">
          <Button
            type="button"
            onClick={() => dismiss('got_it')}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-1.5 font-semibold"
            data-testid="header-install-guide-got-it"
          >
            <Globe className="h-3.5 w-3.5" /> Got it — I&apos;ll use the browser shortcut
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
